import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, MapPin, Send, Download, FileBarChart, ChevronDown, ChevronUp, Save, Newspaper, Award, CalendarRange, Eye, Mail, CheckCircle2, RefreshCw, Settings2, MapPinOff, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { api } from '../../lib/api';
import { StatusBadge, formatDate, categoryLabel, STATUS_LABELS } from '../../components/Shared';
import { pushpinIcon } from '../../components/MapPushpin';
import { PinPlacer } from '../../components/PinPlacer';
import { PhotoGrid } from '../../components/PhotoLightbox';
import { SharePreview } from '../../components/SharePreview';
import { toast } from 'sonner';

const RADIUS_LABELS = { '250m': '250 m (same block)', '500m': '500 m (neighbourhood)', '1km': '1 km (wider area)', ward_category: 'Same ward / same category' };

// ---------------------------------------------------------------- weekly digest panel
const DAY_OPTIONS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DigestAutoSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/tracker-digest/settings').then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { last_auto_week, ...payload } = cfg;
      const r = await api.put('/admin/tracker-digest/settings', payload);
      setCfg(r.data);
      toast.success(r.data.enabled
        ? 'Auto-draft ON — a draft will be created for review. Nothing publishes or emails automatically.'
        : 'Weekly digest settings saved (auto-draft is OFF)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  if (!cfg) return null;
  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="digest-settings">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs font-bold text-[#1a2744]" data-testid="digest-settings-toggle">
        <Settings2 className="h-3.5 w-3.5 text-[#C9A84C]" /> Weekly Digest Settings {cfg.enabled && <span className="text-emerald-700">(Auto-draft ON)</span>}
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
            <div>
              <p className="text-sm font-bold text-[#1a2744]">Auto-create Monday draft</p>
              <p className="text-xs text-slate-500 mt-0.5">Creates a draft for review only. Never publishes, emails, posts, or notifies residents.</p>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="digest-auto-toggle" />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Day of week</label>
              <Select value={cfg.day_of_week} onValueChange={(v) => set('day_of_week', v)}>
                <SelectTrigger className="h-10 text-xs bg-white" data-testid="digest-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAY_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Time (Toronto)</label>
              <Input type="time" value={cfg.time} onChange={(e) => set('time', e.target.value)} className="h-10 text-xs bg-white" data-testid="digest-time-input" />
            </div>
            <div className="col-span-2">
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Week range</label>
              <Select value={cfg.week_range} onValueChange={(v) => set('week_range', v)}>
                <SelectTrigger className="h-10 text-xs bg-white" data-testid="digest-range-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="previous">Previous full week (Mon–Sun)</SelectItem>
                  <SelectItem value="current">Current week so far</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Default title format</label>
            <Input value={cfg.title_format} onChange={(e) => set('title_format', e.target.value)} className="h-10 text-xs bg-white" data-testid="digest-title-format" />
            <p className="text-[10px] text-slate-400 mt-1">{'{Week Range}'} is replaced with the week dates.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
              <p className="text-xs font-bold text-[#1a2744]">Also attach to a newsletter draft</p>
              <Switch checked={cfg.attach_newsletter} onCheckedChange={(v) => set('attach_newsletter', v)} data-testid="digest-attach-toggle" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
              <p className="text-xs font-bold text-[#1a2744]">Dashboard notification</p>
              <Switch checked={cfg.dashboard_notification} onCheckedChange={(v) => set('dashboard_notification', v)} data-testid="digest-notify-toggle" />
            </label>
          </div>
          <Button onClick={save} disabled={busy} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="digest-settings-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Digest Settings
          </Button>
        </div>
      )}
    </div>
  );
};

const EMAIL_STATE_STYLES = {
  queued: 'text-[#24406f] bg-[#EAF2FF] border-[#CFE0FF]',
  held: 'text-amber-800 bg-amber-50 border-amber-200',
  sent: 'text-emerald-800 bg-emerald-50 border-emerald-200',
  failed: 'text-red-700 bg-red-50 border-red-200',
};

const ResidentEmailPanel = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState('');
  const [emails, setEmails] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [busy, setBusy] = useState('');
  const [summary, setSummary] = useState(null);

  const loadCfg = () => api.get('/admin/email-settings').then((r) => setCfg(r.data)).catch(() => {});
  const loadSummary = () => api.get('/admin/weekly-summary').then((r) => setSummary(r.data)).catch(() => {});
  const loadEmails = useCallback((f) => {
    api.get(`/admin/issue-emails?state=${f || 'all'}`).then((r) => setEmails(Array.isArray(r.data) ? r.data : (r.data?.rows || []))).catch(() => {});
  }, []);
  useEffect(() => { loadCfg(); loadSummary(); }, []);
  useEffect(() => { if (open) loadEmails(stateFilter); }, [open, stateFilter, loadEmails]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const toggleSummary = async (enabled) => {
    try {
      const r = await api.put('/admin/weekly-summary', { enabled });
      setSummary(r.data);
      toast.success(enabled
        ? 'Weekly summary ON. It arrives Mondays at 8:00 AM Toronto time.'
        : 'Weekly summary OFF. Nothing will be scheduled.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the weekly summary'); }
  };

  const sendSummaryNow = async () => {
    setBusy('summary');
    try {
      const r = await api.post('/admin/weekly-summary/send-now');
      if (r.data.status === 'sent') toast.success('Weekly summary sent to your notification email.');
      else toast.warning(`Weekly summary recorded as ${r.data.status}. Check the mail connection.`);
      loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setBusy('');
  };

  const save = async () => {
    setBusy('save');
    try {
      const r = await api.put('/admin/email-settings', {
        host: cfg.host || '', port: cfg.port || '465', username: cfg.username || '',
        sender: cfg.sender || '', sender_name: cfg.sender_name || '', password,
      });
      setCfg(r.data); setPassword('');
      toast.success('Mail settings saved. Password is stored securely and never shown again.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const verify = async () => {
    setBusy('verify');
    try {
      const r = await api.post('/admin/email-settings/verify');
      if (r.data.ok) toast.success('SMTP connection verified. You can activate sending.');
      else toast.error(r.data.error || 'Verification failed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    setBusy('');
  };

  const activate = async () => {
    setBusy('activate');
    try {
      const r = await api.post('/admin/email-settings/activate');
      setCfg(r.data);
      toast.success(`Sending is ON for future status changes. ${r.data.backlog_held || 0} queued update(s) moved to Held (never auto-sent).`);
      loadEmails(stateFilter);
    } catch (e) { toast.error(e.response?.data?.detail || 'Activation failed'); }
    setBusy('');
  };

  const deactivate = async () => {
    setBusy('deactivate');
    try {
      const r = await api.post('/admin/email-settings/deactivate');
      setCfg(r.data);
      toast.success('Sending is OFF. New status updates will queue instead.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };

  const sendNow = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/issue-emails/${id}/send`);
      toast.success('Email sent to the resident.');
      loadEmails(stateFilter); loadCfg();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setBusy('');
  };

  if (!cfg) return null;
  const counts = cfg.counts || {};
  const statusChip = cfg.activated
    ? <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="resident-email-status-chip">Sending ON</span>
    : cfg.configured
      ? <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="resident-email-status-chip">Configured, sending OFF</span>
      : <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="resident-email-status-chip">Not configured, updates queue safely</span>;

  return (
    <Card className="mb-4"><CardContent className="p-4" data-testid="resident-email-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="resident-email-toggle">
        <Mail className="h-4 w-4 text-[#C9A84C]" />
        <span className="font-bold text-[#1a2744] text-sm flex-1">Resident Email Updates (status changes)</span>
        {statusChip}
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-slate-500">Residents get an email on every status change: tracking number, new status, the public update note, and a track link. Internal notes are never sent. Every email includes an unsubscribe link that does not affect tracking.</p>
          <p className="text-xs font-bold text-[#1a2744]" data-testid="email-settings-page-link-note">Full setup with encryption, reply-to, a test email and the complete log: <Link to="/admin/email-settings" className="underline decoration-[#C9A84C] decoration-2 underline-offset-2" data-testid="email-settings-page-link">Admin &gt; Email Settings</Link></p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="font-bold text-[#1a2744] text-sm">Mail server (SMTP) setup</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="SMTP host"><Input value={cfg.host || ''} onChange={(e) => set('host', e.target.value)} placeholder="mail.stevelesperance.com" className="h-10 bg-white" data-testid="smtp-host-input" /></F>
              <F label="Port"><Input value={cfg.port || ''} onChange={(e) => set('port', e.target.value)} placeholder="465" className="h-10 bg-white" data-testid="smtp-port-input" /></F>
              <F label="Username (mailbox)"><Input value={cfg.username || ''} onChange={(e) => set('username', e.target.value)} placeholder="info@stevelesperance.com" className="h-10 bg-white" data-testid="smtp-username-input" /></F>
              <F label={cfg.has_password ? 'Password (saved, enter to replace)' : 'Password'}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={cfg.has_password ? 'Leave blank to keep the saved password' : 'Mailbox password'} className="h-10 bg-white" data-testid="smtp-password-input" autoComplete="new-password" />
              </F>
              <F label="Sender email (From)"><Input value={cfg.sender || ''} onChange={(e) => set('sender', e.target.value)} placeholder="info@stevelesperance.com" className="h-10 bg-white" data-testid="smtp-sender-input" /></F>
              <F label="Sender name"><Input value={cfg.sender_name || ''} onChange={(e) => set('sender_name', e.target.value)} placeholder="Steve Lesperance, Ward 8" className="h-10 bg-white" data-testid="smtp-sender-name-input" /></F>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={busy === 'save'} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="smtp-save-button">
                {busy === 'save' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Mail Settings
              </Button>
              <Button onClick={verify} disabled={busy === 'verify' || !cfg.configured} variant="outline" className="h-10 font-bold btn-press" data-testid="smtp-verify-button">
                {busy === 'verify' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Verify Connection
              </Button>
              {cfg.activated ? (
                <Button onClick={deactivate} disabled={busy === 'deactivate'} variant="outline" className="h-10 text-red-600 border-red-200 font-bold btn-press" data-testid="smtp-deactivate-button">Turn Sending Off</Button>
              ) : (
                <Button onClick={activate} disabled={busy === 'activate' || !cfg.configured} variant="outline" className="h-10 border-emerald-400 text-emerald-800 font-bold btn-press" data-testid="smtp-activate-button">Activate Sending</Button>
              )}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="smtp-backlog-note">Activation only affects future status changes. Anything already queued becomes <strong>Held</strong> and is never sent automatically. You can send held updates one at a time below.</p>
          </div>

          {summary && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2" data-testid="weekly-summary-setting">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <p className="font-bold text-[#1a2744] text-sm">Weekly admin summary (Mondays 8:00 AM Toronto)</p>
                  <p className="text-xs text-slate-500 mt-0.5">A Monday email to your notification address with overdue reports, follow-ups due this week, reports needing a verified location, pending resident emails, and last week's new report count. Independent of the resident sending switch above. One digest per week.</p>
                </div>
                <Switch checked={summary.enabled} onCheckedChange={toggleSummary} data-testid="weekly-summary-toggle" />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button onClick={sendSummaryNow} disabled={busy === 'summary'} variant="outline" className="h-9 text-xs font-bold btn-press" data-testid="weekly-summary-send-now">
                  {busy === 'summary' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Send Test Now
                </Button>
                {summary.last_sent_at && (
                  <span className="text-[11px] text-slate-500" data-testid="weekly-summary-last">
                    Last: {formatDate(summary.last_sent_at)} ({summary.last_status || 'unknown'})
                  </span>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <p className="font-bold text-[#1a2744] text-sm flex-1">Email log</p>
              {['all', 'queued', 'held', 'sent', 'failed'].map((s) => (
                <button key={s} onClick={() => setStateFilter(s)}
                  className={`text-[11px] font-bold rounded-full px-2.5 py-1 border ${stateFilter === s ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`email-filter-${s}`}>
                  {s === 'all' ? 'All' : `${s[0].toUpperCase() + s.slice(1)} (${counts[s] ?? 0})`}
                </button>
              ))}
            </div>
            {emails.length === 0 ? (
              <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg p-4 text-center" data-testid="email-log-empty">No status emails recorded yet. They appear here when a report status changes.</p>
            ) : (
              <div className="space-y-2" data-testid="email-log-list">
                {emails.map((m) => (
                  <div key={m.id} className="border border-slate-100 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="email-log-row">
                    <span className="font-mono text-xs font-bold text-[#C9A84C]">{m.tracking_number}</span>
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${EMAIL_STATE_STYLES[m.state] || EMAIL_STATE_STYLES.queued}`} data-testid={`email-state-${m.state}`}>{(m.state || '').toUpperCase()}</span>
                    <span className="text-xs text-slate-600 flex-1 min-w-[180px]">{m.status_label} to {m.to_email}</span>
                    <span className="text-[11px] text-slate-400">{formatDate(m.created_at)}</span>
                    {m.state !== 'sent' && (
                      <Button size="sm" onClick={() => sendNow(m.id)} disabled={busy === m.id || !cfg.configured} variant="outline" className="h-8 text-xs font-bold btn-press" data-testid={`email-send-now-${m.tracking_number}`}>
                        {busy === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />} Send now
                      </Button>
                    )}
                    {m.error && <p className="w-full text-[11px] text-red-600" data-testid="email-error-text">{m.error}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </CardContent></Card>
  );
};

const WeeklyDigestPanel = () => {
  const [weekStart, setWeekStart] = useState('');
  const [digests, setDigests] = useState([]);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);

  const load = () => api.get('/admin/tracker-digest/weekly').then((r) => setDigests(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy('gen');
    try {
      const r = await api.post('/admin/tracker-digest/weekly', weekStart ? { week_start: weekStart } : {});
      toast.success(`Draft created: "${r.data.title}" — nothing is emailed or published automatically.`);
      setPreview(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate the weekly digest'); }
    setBusy('');
  };
  const toNewsletter = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/admin/tracker-digest/weekly/${d.id}/newsletter`);
      toast.success('Draft newsletter created — review it in the Newsletter manager. Nothing is sent automatically.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const markReady = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/admin/tracker-digest/weekly/${d.id}/ready`);
      toast.success('Marked ready for review — still a draft until you publish it.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const openPreview = async (d) => {
    try {
      const r = await api.post('/admin/tracker-digest/weekly', { week_start: d.digest_week });
      setPreview(r.data);
    } catch { toast.error('Could not load the preview'); }
  };

  return (
    <Card className="mb-4" id="weekly-digest" data-testid="weekly-digest-panel">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><CalendarRange className="h-4 w-4 text-[#C9A84C]" /> Weekly Digest</p>
            <p className="text-xs text-slate-500 mt-0.5">Creates a draft Newsroom article + optional draft newsletter. Privacy-safe public data only — nothing sends or publishes automatically.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="h-10 w-40 bg-white" title="Pick any date in the week (optional — defaults to this week)" data-testid="digest-week-input" />
            <Button onClick={generate} disabled={busy === 'gen'} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="generate-weekly-digest-button">
              {busy === 'gen' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-1.5" />} Generate Weekly Digest
            </Button>
          </div>
        </div>
        {digests.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="weekly-digest-list">
            {digests.map((d) => (
              <div key={d.id} className="rounded-xl border border-slate-200 p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-2" data-testid={`weekly-digest-${d.id}`}>
                <div className="min-w-0">
                  <p className="font-bold text-[#1a2744] text-sm truncate">{d.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Draft{d.review_status === 'needs_review' ? ' · Ready for review' : ''}{d.newsletter_id ? ' · Newsletter draft attached' : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openPreview(d)} className="h-8 text-xs font-bold" data-testid={`digest-preview-${d.id}`}><Eye className="h-3 w-3 mr-1" /> Preview</Button>
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`digest-edit-${d.id}`}>
                    <Link to="/admin/newsroom"><Newspaper className="h-3 w-3 mr-1" /> Edit in Newsroom</Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toNewsletter(d)} disabled={busy === d.id} className="h-8 text-xs font-bold" data-testid={`digest-newsletter-${d.id}`}><Mail className="h-3 w-3 mr-1" /> Attach to Newsletter Draft</Button>
                  <Button size="sm" variant="outline" onClick={() => markReady(d)} disabled={busy === d.id || d.review_status === 'needs_review'} className="h-8 text-xs font-bold text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`digest-ready-${d.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Mark Ready</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="digest-preview-dialog">
            <DialogHeader><DialogTitle className="font-serif text-[#1a2744] pr-6">{preview?.title}</DialogTitle></DialogHeader>
            <div className="prose prose-sm max-w-none text-slate-700 [&_h2]:font-serif [&_h2]:text-[#1a2744] [&_a]:text-[#1a2744] [&_a]:font-bold" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview?.body_html || '') }} />
            <p className="text-xs text-slate-400 border-t pt-3">Draft only. Edit and publish from the Newsroom manager when ready.</p>
          </DialogContent>
        </Dialog>
        <DigestAutoSettings />
      </CardContent>
    </Card>
  );
};

/* Field wrapper. Defined at module scope ON PURPOSE: defining it inside the
   page component recreated it on every keystroke, remounting the inputs and
   making them lose focus after each letter. */
/* Inline 'Add verified correction' for a flagged row in the repair preview:
   the admin supplies a verified address or exact coordinates plus a source
   note. NOTHING changes until Confirm & Apply - the correction simply appears
   as a green approved-on-apply row. */
const AddVerifiedCorrection = ({ row, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [addr, setAddr] = useState('');
  const [comm, setComm] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!source.trim()) { toast.error('Add a short source note (how was this verified?)'); return; }
    const payload = { source };
    if (lat && lng) { payload.lat = Number(lat); payload.lng = Number(lng); }
    else if (addr) { payload.address = addr; payload.community = comm; }
    else { toast.error('Provide an address or exact coordinates'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/issues/${row.issue_id}/suggest-location`, payload);
      toast.success(r.data.ward ? `Verified correction saved — Ward ${r.data.ward}` : 'Verified correction saved (ward to confirm separately)');
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the correction');
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="block mt-1 text-[10px] font-bold text-emerald-800 underline" data-testid={`add-correction-${row.tracking_number}`}>
        Add verified correction
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-1.5 bg-emerald-50/60 border border-emerald-200 rounded-lg p-2" data-testid={`correction-form-${row.tracking_number}`}>
      <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Verified address (e.g. 1473 Highway 7A)" className="h-8 text-[11px]" data-testid="correction-address-input" />
      <Input value={comm} onChange={(e) => setComm(e.target.value)} placeholder="Community (e.g. Bethany)" className="h-8 text-[11px]" data-testid="correction-community-input" />
      <div className="grid grid-cols-2 gap-1.5">
        <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="or Latitude" className="h-8 text-[11px]" data-testid="correction-lat-input" />
        <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Longitude" className="h-8 text-[11px]" data-testid="correction-lng-input" />
      </div>
      <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source note (required — how was this verified?)" className="h-8 text-[11px]" data-testid="correction-source-input" />
      <div className="flex gap-1.5">
        <Button size="sm" disabled={busy} onClick={save} className="h-7 text-[11px] font-bold bg-emerald-700 hover:bg-emerald-800 text-white btn-press" data-testid="correction-save-button">
          {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Save correction
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-7 text-[11px] font-bold btn-press" data-testid="correction-cancel-button">Cancel</Button>
      </div>
    </div>
  );
};

/* Comparison map for the Re-geocode preview: shows every report's current
   (possibly wrong) position in grey, the proposed corrected exact position in
   green, manually-verified preserved positions in violet, and dashed lines
   connecting old -> proposed so each correction is visible at a glance.
   Review-flagged reports show only their grey current pin (or none). */
const RegeocodeComparisonMap = ({ rows }) => {
  const points = [];
  const markers = [];
  const lines = [];
  (rows || []).forEach((r) => {
    const oldPos = r.old_lat != null && r.old_lng != null ? [Number(r.old_lat), Number(r.old_lng)] : null;
    const newPos = r.new_exact_lat != null && r.new_exact_lng != null ? [Number(r.new_exact_lat), Number(r.new_exact_lng)] : null;
    const oldColor = r.manual_hold ? '#7c3aed' : '#64748b';
    if (oldPos) {
      points.push(oldPos);
      markers.push(
        <Marker key={`old-${r.issue_id}`} position={oldPos} icon={pushpinIcon(oldColor, 24)} opacity={r.manual_hold ? 1 : 0.85}>
          <Popup>
            <div className="text-xs">
              <p className="font-bold">{r.tracking_number}</p>
              <p>{r.manual_hold ? 'Manually verified location (preserved)' : 'Current mapped position'}</p>
              {r.needs_review && !r.manual_hold && <p className="text-amber-700 font-bold">Needs manual review — public pin is removed until placed correctly</p>}
            </div>
          </Popup>
        </Marker>,
      );
    }
    if (newPos && !r.manual_hold) {
      const proposedColor = r.needs_review ? '#d97706' : '#16a34a';
      points.push(newPos);
      markers.push(
        <Marker key={`new-${r.issue_id}`} position={newPos} icon={pushpinIcon(proposedColor, 30)}>
          <Popup>
            <div className="text-xs">
              <p className="font-bold">{r.tracking_number}</p>
              {r.needs_review
                ? <p className="text-amber-700 font-bold">Found position — needs your confirmation before it is used</p>
                : <p className="text-emerald-700 font-bold">Proposed corrected position</p>}
              <p>{r.ward ? `Ward ${r.ward}` : ''} {r.confidence ? `· ${r.confidence} confidence` : ''}</p>
              {r.warning && <p className="text-slate-600 mt-1">{r.warning}</p>}
            </div>
          </Popup>
        </Marker>,
      );
      if (oldPos) {
        lines.push(<Polyline key={`ln-${r.issue_id}`} positions={[oldPos, newPos]} pathOptions={{ color: proposedColor, weight: 2, dashArray: '6 6', opacity: 0.7 }} />);
      }
    }
  });

  if (points.length === 0) {
    return (
      <p className="text-xs text-slate-500 border border-slate-200 rounded-xl p-3" data-testid="regeocode-map-empty">
        No positions to compare on the map yet: every report is either awaiting manual placement (no trustworthy coordinates) or has no stored position.
      </p>
    );
  }

  const bounds = L.latLngBounds(points).pad(0.25);
  return (
    <div className="space-y-1.5" data-testid="regeocode-comparison-map">
      <div className="h-72 w-full rounded-xl overflow-hidden border border-slate-200">
        <MapContainer bounds={bounds} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {lines}
          {markers}
        </MapContainer>
      </div>
      <p className="text-[11px] text-slate-500">
        <span className="font-bold text-slate-600">Grey pin</span> = current position ·{' '}
        <span className="font-bold text-emerald-700">Green pin</span> = proposed corrected position ·{' '}
        <span className="font-bold text-amber-700">Amber pin</span> = found position that needs your confirmation ·{' '}
        <span className="font-bold text-violet-700">Violet pin</span> = manually verified, preserved ·{' '}
        dashed line shows the move. Reports flagged for review keep a grey pin here but their public pin is removed until placed correctly.
      </p>
    </div>
  );
};

/* Phase 109B: Pin location audit. Traces every report with saved coordinates
   to its submitted address/intersection and shows whether the saved point is
   VERIFIED or a LEGACY community-centre fallback from the original build.
   Legacy pins can be quarantined (hidden from the public map only, nothing
   deleted) after explicit confirmation, until Fix location / a verified
   correction places them properly. */
const PinAuditPanel = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quarantining, setQuarantining] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/issues/legacy-pin-audit', { timeout: 120000 });
      setAudit(r.data);
      setSelected(r.data.quarantine_candidates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally { setBusy(false); }
  };

  const quarantine = async () => {
    setConfirmOpen(false);
    setQuarantining(true);
    try {
      const r = await api.post('/admin/issues/legacy-pin-audit/quarantine', { issue_ids: selected, confirm: true });
      toast.success(`${r.data.quarantined.length} pin(s) hidden from the public map until verified${r.data.refused.length ? `; ${r.data.refused.length} refused` : ''}.`);
      await run();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Quarantine failed');
    } finally { setQuarantining(false); }
  };

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const kindBadge = (r) => {
    if (r.kind === 'verified') return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-[10px] font-bold" data-testid="pin-audit-kind-verified"><ShieldCheck className="h-3 w-3" /> Verified</span>;
    if (r.kind === 'legacy_unverified') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-900 border border-amber-300 px-2 py-0.5 text-[10px] font-bold" data-testid="pin-audit-kind-legacy"><AlertTriangle className="h-3 w-3" /> Legacy fallback</span>;
    if (r.kind === 'unmapped') return <span className="rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 text-[10px] font-bold">Not on map</span>;
    return <span className="rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-[10px] font-bold">Review</span>;
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); if (!audit) run(); }} className="h-11 font-bold btn-press border-amber-500 text-amber-900" data-testid="pin-audit-button">
        <MapPinOff className="h-4 w-4 mr-1.5" /> Pin Location Audit
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="pin-audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1a2744]">Pin Location Audit</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The original build placed public pins at an approximate community centre point plus about 1 km of random offset. Those saved coordinates are still on some reports. This audit traces every report to its submitted address or intersection and flags saved positions that are not verified. Nothing here changes a location: use <strong>Fix location</strong> or a <strong>verified correction</strong> in the Re-geocode preview to place a pin properly. Quarantine only hides a wrong pin from the public map until then.
          </p>
          {busy && <div className="flex items-center gap-2 py-8 justify-center text-slate-500" data-testid="pin-audit-loading"><Loader2 className="h-4 w-4 animate-spin" /> Checking every report...</div>}
          {audit && !busy && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="pin-audit-summary">
                <span className="bg-slate-100 rounded-full px-2.5 py-1">{audit.total} report(s)</span>
                <span className="bg-slate-100 rounded-full px-2.5 py-1">{audit.public_pins} on the public map</span>
                <span className="bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-full px-2.5 py-1">{audit.verified} verified</span>
                <span className={`rounded-full px-2.5 py-1 border ${audit.legacy_unverified_public ? 'bg-amber-50 text-amber-900 border-amber-300' : 'bg-slate-100 border-slate-200'}`} data-testid="pin-audit-legacy-count">{audit.legacy_unverified_public} public pin(s) at unverified legacy coordinates</span>
                <span className="bg-sky-50 text-sky-900 border border-sky-200 rounded-full px-2.5 py-1">{audit.with_proposed_correction} with a verified correction ready</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="pin-audit-table">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-1.5 pr-2">Hide</th><th className="py-1.5 pr-3">Report</th><th className="py-1.5 pr-3">Submitted location</th><th className="py-1.5 pr-3">Saved position</th><th className="py-1.5 pr-3">Status</th><th className="py-1.5">Why / what to do</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.rows.map((r) => (
                      <tr key={r.issue_id} className="border-t border-slate-100 align-top" data-testid={`pin-audit-row-${r.tracking_number}`}>
                        <td className="py-2 pr-2">
                          {r.kind === 'legacy_unverified' && r.public_visible ? (
                            <Checkbox checked={selected.includes(r.issue_id)} onCheckedChange={() => toggle(r.issue_id)} aria-label={`Quarantine ${r.tracking_number}`} data-testid={`pin-audit-select-${r.tracking_number}`} />
                          ) : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="py-2 pr-3 font-mono font-bold text-[#1a2744] whitespace-nowrap">{r.tracking_number}<div className="text-[10px] font-sans font-normal text-slate-500">{r.community || 'no community'}</div></td>
                        <td className="py-2 pr-3 text-slate-700 max-w-[220px]">
                          {r.submitted_location || <span className="text-slate-400">none given</span>}
                          {r.submitted_address_private && <div className="text-[10px] text-slate-500">+ private street address on file</div>}
                        </td>
                        <td className="py-2 pr-3 font-mono text-[10px] text-slate-600 whitespace-nowrap">
                          {r.saved_lat != null ? `${Number(r.saved_lat).toFixed(5)}, ${Number(r.saved_lng).toFixed(5)}` : '-'}
                          {r.legacy_centre && <div className="text-amber-800 font-sans">{r.legacy_centre_distance_m} m from old "{r.legacy_centre}" centre</div>}
                          <div className="font-sans text-slate-500">{r.public_visible ? 'public' : 'hidden'} · {r.ward ? `Ward ${r.ward}` : 'ward unconfirmed'}</div>
                        </td>
                        <td className="py-2 pr-3">{kindBadge(r)}</td>
                        <td className="py-2 text-slate-700 max-w-[280px]">
                          {r.proposed_correction && (
                            <div className="mb-1 rounded-lg border border-emerald-300 bg-emerald-50 p-1.5 text-emerald-900" data-testid={`pin-audit-correction-${r.tracking_number}`}>
                              <strong>Verified correction ready:</strong> {r.proposed_correction.display_name} ({Number(r.proposed_correction.lat).toFixed(5)}, {Number(r.proposed_correction.lng).toFixed(5)}){r.proposed_correction.ward ? `, Ward ${r.proposed_correction.ward}` : ', ward needs confirmation'}
                            </div>
                          )}
                          {r.why && <div className="text-slate-600">{r.why}</div>}
                          <div className="font-semibold text-[#1a2744] mt-0.5">{r.action}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-500">Quarantine hides the selected pins from the public map only. Reports, tracking numbers, photos and saved coordinates are kept and the pin returns as soon as it is placed properly.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={run} disabled={busy} className="h-10 font-bold btn-press" data-testid="pin-audit-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Re-check</Button>
                  <Button onClick={() => setConfirmOpen(true)} disabled={!selected.length || quarantining} className="h-10 bg-amber-700 hover:bg-amber-800 text-white font-bold btn-press" data-testid="pin-audit-quarantine-button">
                    {quarantining ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPinOff className="h-4 w-4 mr-1.5" />} Hide {selected.length} unverified pin(s) from public map
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="pin-audit-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Hide {selected.length} unverified pin(s) from the public map?</AlertDialogTitle>
            <AlertDialogDescription>
              These reports keep their tracking numbers, content and saved coordinates. They simply stop showing on the public map until you place them with Fix location or approve a verified correction. Manually verified pins are never affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pin-audit-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={quarantine} className="bg-amber-700 hover:bg-amber-800" data-testid="pin-audit-confirm-button">Hide from public map</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/* Re-geocode All Reports (Phase 97): preview table -> explicit confirm ->
   apply (originals backed up on each issue) -> revert available. */
const RegeocodePanel = ({ onApplied }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const runPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.post('/admin/issues/regeocode/preview', {}, { timeout: 300000 });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview?.proposal_id) return;
    if (!window.confirm(`Apply corrected locations to ${preview.total} report(s)? Originals are backed up and can be reverted. Tracking numbers never change.`)) return;
    setApplying(true);
    try {
      const r = await api.post('/admin/issues/regeocode/apply', { proposal_id: preview.proposal_id });
      toast.success(`Corrections applied to ${r.data.applied} report(s). Originals backed up.`);
      setOpen(false);
      setPreview(null);
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  const revert = async () => {
    if (!window.confirm('Restore every report to its original mapped location?')) return;
    try {
      const r = await api.post('/admin/issues/regeocode/revert');
      toast.success(`Restored ${r.data.reverted} report(s) to their original locations.`);
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); if (!preview) runPreview(); }} className="h-11 font-bold btn-press border-[#1a2744] text-[#1a2744]" data-testid="regeocode-all-button">
        <MapPin className="h-4 w-4 mr-1.5" /> Re-geocode All Reports
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="regeocode-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1a2744]">Re-geocode All Reports — Preview</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Every report is re-checked against its complete submitted address or intersection, with the ward detected from the official CKL ward boundaries. <strong>Nothing changes until you confirm.</strong> Originals are backed up and reversible.</p>
          {busy && (
            <div className="flex items-center gap-2 py-8 justify-center text-slate-500" data-testid="regeocode-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Geocoding each report (about 1–2 seconds per report)…
            </div>
          )}
          {preview && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs font-bold">
                <span className="bg-slate-100 rounded-full px-2.5 py-1" data-testid="regeocode-total">{preview.total} report(s)</span>
                <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1">{preview.mappable} mappable</span>
                <span className="bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1">{preview.manual_review} manual review</span>
                {preview.manually_preserved > 0 && (
                  <span className="bg-violet-50 text-violet-800 border border-violet-200 rounded-full px-2.5 py-1" data-testid="regeocode-preserved-count">{preview.manually_preserved} manually verified (preserved)</span>
                )}
                {preview.verified_corrections > 0 && (
                  <span className="bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-full px-2.5 py-1" data-testid="regeocode-verified-count">{preview.verified_corrections} verified correction(s) ready for approval</span>
                )}
                <span className="bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-1">{preview.outside_ward8} outside Ward 8</span>
              </div>

              {/* Comparison map: grey = current (old) position, green = proposed
                  corrected exact position, violet = manually verified preserved.
                  Dashed lines connect old -> proposed for corrected reports. */}
              <RegeocodeComparisonMap rows={preview.rows} />
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-xs" data-testid="regeocode-table">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="p-2 font-bold text-slate-600">Tracking #</th>
                      <th className="p-2 font-bold text-slate-600">Submitted location</th>
                      <th className="p-2 font-bold text-slate-600">Old mapped</th>
                      <th className="p-2 font-bold text-slate-600">Proposed</th>
                      <th className="p-2 font-bold text-slate-600">Ward</th>
                      <th className="p-2 font-bold text-slate-600">Confidence</th>
                      <th className="p-2 font-bold text-slate-600">Warning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.issue_id} className="border-t border-slate-100 align-top" data-testid="regeocode-row">
                        <td className="p-2 font-mono font-bold text-[#1a2744] whitespace-nowrap">
                          {r.tracking_number}
                          {r.manual_hold && <span className="block mt-1 font-sans font-bold text-[10px] text-violet-800 bg-violet-50 border border-violet-200 rounded-full px-1.5 py-0.5 whitespace-nowrap" data-testid="regeocode-manual-hold-badge">Manually verified · preserved</span>}
                          {r.suggested && <span className="block mt-1 font-sans font-bold text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-1.5 py-0.5 whitespace-nowrap" data-testid="regeocode-suggested-badge">Verified correction · applies on confirm</span>}
                        </td>
                        <td className="p-2 text-slate-700 max-w-[180px]">{r.submitted_location || '—'}</td>
                        <td className="p-2 text-slate-500 whitespace-nowrap">{r.old_lat != null ? `${Number(r.old_lat).toFixed(5)}, ${Number(r.old_lng).toFixed(5)}` : '—'}</td>
                        <td className="p-2 text-slate-700 whitespace-nowrap">{r.new_exact_lat != null ? `${Number(r.new_exact_lat).toFixed(5)}, ${Number(r.new_exact_lng).toFixed(5)}` : '—'}</td>
                        <td className="p-2 font-bold whitespace-nowrap">{r.ward ? `Ward ${r.ward}` : (r.needs_ward_confirm ? 'to confirm' : '—')}</td>
                        <td className="p-2">
                          <span className={`font-bold rounded-full px-2 py-0.5 border ${r.confidence === 'high' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : r.confidence === 'medium' ? 'bg-[#EAF2FF] text-[#24406f] border-[#CFE0FF]' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>{r.confidence}</span>
                        </td>
                        <td className="p-2 text-amber-800 max-w-[200px]">
                          {r.warning || '—'}
                          {r.needs_review && !r.manual_hold && (
                            <AddVerifiedCorrection row={r} onSaved={runPreview} />
                          )}
                          {r.suggested && (
                            <button
                              type="button"
                              onClick={async () => { try { await api.delete(`/admin/issues/${r.issue_id}/suggest-location`); toast.success('Verified correction removed'); runPreview(); } catch { toast.error('Could not remove'); } }}
                              className="block mt-1 text-[10px] font-bold text-slate-500 underline"
                              data-testid={`remove-suggestion-${r.tracking_number}`}
                            >
                              Remove this correction
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <Button variant="outline" onClick={revert} className="h-11 text-red-700 border-red-200 hover:bg-red-50 font-bold btn-press" data-testid="regeocode-revert-button">Revert previous corrections</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} className="h-11 font-bold btn-press" data-testid="regeocode-cancel-button">Cancel</Button>
                  <Button onClick={apply} disabled={applying} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="regeocode-apply-button">
                    {applying && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Confirm & Apply Corrections
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/* Manual location review (Phase 97 + click-to-place Phase 101): correct the
   address and re-check, enter exact coordinates, or click/drag a pin on the
   map. A live preview shows the detected ward and an example generalized
   public position BEFORE anything is saved. */

/* Per-report email history + "Resend confirmation" (Phase 114). */
const IssueEmailHistory = ({ detail }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/issues/${detail.id}/emails`); setRows(r.data); } catch { setRows([]); }
  }, [detail.id]);
  useEffect(() => { load(); }, [load]);
  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/issues/${detail.id}/resend-receipt`);
      (r.data.ok ? toast.success : toast.warning)(r.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Resend failed'); } finally { setBusy(false); }
  };
  const [sendingId, setSendingId] = useState('');
  const sendNow = async (row, force = false) => {
    setSendingId(row.id);
    try {
      const r = await api.post(`/admin/issue-emails/${row.id}/retry`, { force });
      (r.data.simulated ? toast.warning : toast.success)(r.data.simulated ? 'Simulated only: no real email was sent' : 'Accepted by the mail server (not proof of inbox delivery)');
      load();
    } catch (err) {
      const d = err.response?.data?.detail || 'Send failed';
      if (err.response?.status === 409 && !force && window.confirm(`${d}\n\nSend anyway?`)) return sendNow(row, true);
      toast.error(d);
    } finally { setSendingId(''); }
  };
  const chip = (st) => ({ sent: 'bg-emerald-50 text-emerald-800 border-emerald-300', failed: 'bg-red-50 text-red-800 border-red-300', queued: 'bg-sky-50 text-sky-800 border-sky-200', held: 'bg-amber-50 text-amber-900 border-amber-300', disabled: 'bg-slate-100 text-slate-600 border-slate-200', simulated: 'bg-violet-50 text-violet-800 border-violet-300' }[st] || 'bg-slate-100 text-slate-600 border-slate-200');
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="issue-email-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><Mail className="h-4 w-4" /> Emails to this resident {detail.email ? '' : '(no email on file)'}</p>
        <Button size="sm" variant="outline" onClick={resend} disabled={busy || !detail.email} className="h-9 text-xs font-bold btn-press" data-testid="resend-confirmation-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Resend confirmation
        </Button>
      </div>
      {rows === null && <p className="text-xs text-slate-500">Loading...</p>}
      {rows && rows.length === 0 && <p className="text-xs text-slate-600" data-testid="issue-email-history-empty">No email has been recorded for this report. Use Resend confirmation to send the tracking number and a copy of the complaint.</p>}
      {rows && rows.length > 0 && (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.id} className="text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2" data-testid={`issue-email-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 font-bold ${chip(r.state)}`} data-testid="issue-email-state">{r.state_label}</span>
                <span className="font-semibold text-[#1a2744] capitalize">{r.kind}</span>
                <span className="text-slate-500">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                {r.resend_of && <span className="text-slate-400">resend</span>}
                {r.can_retry && r.kind !== 'test' && r.kind !== 'share' && (
                  <Button size="sm" variant="outline" onClick={() => sendNow(r)} disabled={sendingId === r.id} className="h-8 text-[11px] font-bold ml-auto" data-testid={`issue-email-send-now-${r.id}`}>{sendingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send now'}</Button>
                )}
              </div>
              <p className="text-slate-600 mt-1">{r.held_reason || r.state_meaning}</p>
              {r.error && <p className="text-red-800 mt-1"><strong>Error:</strong> {r.error_explanation || r.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RelocateControls = ({ detail, onDone }) => {
  const [open, setOpen] = useState(false);
  const [addr, setAddr] = useState(detail.address || '');
  const [comm, setComm] = useState(detail.community || '');
  const [lat, setLat] = useState(detail.exact_lat != null ? String(detail.exact_lat) : '');
  const [lng, setLng] = useState(detail.exact_lng != null ? String(detail.exact_lng) : '');
  const [point, setPoint] = useState(detail.exact_lat != null ? { lat: detail.exact_lat, lng: detail.exact_lng } : null);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [wardChoice, setWardChoice] = useState('');
  const [exactPublic, setExactPublic] = useState(!!detail.public_pin_exact);
  const [busy, setBusy] = useState(false);

  const pick = (pLat, pLng) => {
    setPoint({ lat: pLat, lng: pLng });
    setLat(pLat.toFixed(6));
    setLng(pLng.toFixed(6));
  };

  // Typed coordinates update the map pin too
  useEffect(() => {
    const a = parseFloat(lat); const b = parseFloat(lng);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      setPoint((prev) => (prev && Math.abs(prev.lat - a) < 1e-9 && Math.abs(prev.lng - b) < 1e-9 ? prev : { lat: a, lng: b }));
    }
  }, [lat, lng]);

  // Live preview of ward + generalized public position (nothing is saved)
  useEffect(() => {
    if (!open || !point) { setPreviewInfo(null); return undefined; }
    const t = setTimeout(() => {
      api.post('/admin/issues/geo/point-preview', point)
        .then((r) => setPreviewInfo(r.data))
        .catch((err) => setPreviewInfo({ error: err.response?.data?.detail || 'Preview unavailable' }));
    }, 350);
    return () => clearTimeout(t);
  }, [open, point]);

  const run = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/issues/${detail.id}/relocate`, payload);
      if (r.data.needs_ward_confirmation) toast.success('Location set — ward left for your confirmation');
      else toast.success(r.data.ward ? `Location set — Ward ${r.data.ward}` : 'Location updated');
      setOpen(false);
      onDone?.({ ward: r.data.ward, needs_location_review: r.data.needs_location_review, lat: r.data.lat, lng: r.data.lng, address: payload.address ?? detail.address, community: payload.community ?? detail.community });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set the location');
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-2">
      {!open ? (
        <Button size="sm" variant="outline" className="h-8 text-xs font-bold btn-press border-[#1a2744] text-[#1a2744]" onClick={() => setOpen(true)} data-testid="relocate-open-button">
          <MapPin className="h-3 w-3 mr-1" /> Fix location
        </Button>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="relocate-panel">
          <p className="font-bold text-[#1a2744]">Correct the address and re-check:</p>
          <div className="grid grid-cols-2 gap-2">
            <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Street address or intersection" className="h-9 text-xs" data-testid="relocate-address-input" />
            <Input value={comm} onChange={(e) => setComm(e.target.value)} placeholder="Community (e.g. Bethany)" className="h-9 text-xs" data-testid="relocate-community-input" />
          </div>
          <Button size="sm" disabled={busy} onClick={() => run({ address: addr, community: comm, exact_public: exactPublic })} className="h-8 text-xs font-bold bg-[#1a2744] text-white btn-press" data-testid="relocate-recheck-button">
            {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Re-check location
          </Button>

          <p className="font-bold text-[#1a2744] pt-1">Or click the map to place the pin (drag to adjust; Enlarge Map for full screen):</p>
          <div data-testid="relocate-map">
            <PinPlacer value={point} onChange={(p) => { if (p) pick(p.lat, p.lng); else { setPoint(null); setPreviewInfo(null); } }} showExactOption={false} height="h-60" testId="relocate-pin" helpText="Click the map, drag the pin, use an address lookup, or Enlarge Map for a full-screen view." />
            {previewInfo?.generalized_lat != null && <p className="text-[11px] text-slate-500 mt-1">Gold preview pin (public position): {previewInfo.generalized_lat}, {previewInfo.generalized_lng}</p>}
          </div>

          {/* Live preview BEFORE saving */}
          {point && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-1" data-testid="relocate-preview">
              <p><span className="font-bold text-slate-600">Submitted:</span> {[detail.address, detail.location, detail.community].filter(Boolean).join(' / ') || 'no location text provided'}</p>
              <p><span className="font-bold text-slate-600">Selected exact point (admin-only):</span> <span data-testid="relocate-preview-coords">{point.lat.toFixed(6)}, {point.lng.toFixed(6)}</span></p>
              {previewInfo?.error && <p className="text-red-700 font-bold" data-testid="relocate-preview-error">{previewInfo.error}</p>}
              {previewInfo && !previewInfo.error && (
                <>
                  <p data-testid="relocate-preview-ward">
                    <span className="font-bold text-slate-600">Detected ward:</span>{' '}
                    {previewInfo.ward
                      ? <span className="font-bold text-emerald-800">Ward {previewInfo.ward}</span>
                      : <span className="font-bold text-amber-800">Not inside any official ward polygon — the location can still be saved; the ward is confirmed by you, never guessed</span>}
                  </p>
                  {previewInfo.boundary_hint && <p className="text-amber-800" data-testid="relocate-preview-hint">{previewInfo.boundary_hint}</p>}
                  {!previewInfo.ward && (
                    <div className="flex items-center gap-2" data-testid="relocate-ward-confirm">
                      <span className="font-bold text-slate-600">Confirm ward yourself (optional):</span>
                      <select
                        value={wardChoice}
                        onChange={(e) => setWardChoice(e.target.value)}
                        className="h-7 rounded-md border border-slate-300 bg-white px-1.5 text-[11px] font-bold text-[#1a2744]"
                        data-testid="relocate-ward-select"
                      >
                        <option value="">Leave as "to be confirmed"</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => <option key={w} value={w}>Ward {w}</option>)}
                      </select>
                    </div>
                  )}
                  {previewInfo.generalized_lat != null && (
                    <p data-testid="relocate-preview-generalized">
                      <span className="font-bold text-slate-600">Public generalized position (gold pin):</span> {previewInfo.generalized_lat.toFixed(5)}, {previewInfo.generalized_lng.toFixed(5)}
                      <span className="text-slate-500"> — the published pin is re-generalized on save and stays about 100–150 m away{previewInfo.ward ? ', inside the same ward' : ''}. The exact point is never shown publicly.</span>
                    </p>
                  )}
                  {previewInfo.note && <p className="text-slate-500">{previewInfo.note}</p>}
                </>
              )}
            </div>
          )}

          <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs cursor-pointer" data-testid="relocate-exact-public">
            <Checkbox checked={exactPublic} onCheckedChange={(v) => setExactPublic(!!v)} className="mt-0.5" data-testid="relocate-exact-public-checkbox" />
            <span className="text-slate-700"><strong>Public place:</strong> show the public pin exactly at this point (road, intersection, park). Leave unticked for a home or private property so the public pin stays about 100 to 150 m away.</span>
          </label>
          <p className="font-bold text-[#1a2744] pt-1">Or enter exact coordinates:</p>
          <div className="grid grid-cols-2 gap-2">
            <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="Latitude (44.xxxx)" className="h-9 text-xs" data-testid="relocate-lat-input" />
            <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="Longitude (-78.xxxx)" className="h-9 text-xs" data-testid="relocate-lng-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !point} onClick={() => run({ lat: point.lat, lng: point.lng, exact_public: exactPublic, ...(wardChoice ? { ward: Number(wardChoice) } : {}) })} className="h-8 text-xs font-bold bg-[#1a2744] text-white btn-press" data-testid="relocate-coords-button">
              {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Save this location
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs font-bold btn-press" onClick={() => setOpen(false)} data-testid="relocate-cancel-button">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, children }) => (
  <div>
    <label className="block font-bold text-[#1a2744] mb-1 text-xs uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

export default function AdminIssues() {
  const [searchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ status: 'all', category: 'all', community: 'all', visibility: 'all', date_from: '', date_to: '', search: searchParams.get('search') || '' });
  const [categories, setCategories] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [notePublic, setNotePublic] = useState(true);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [trackerCfg, setTrackerCfg] = useState({ similar_radius_mode: '500m', radius_options: ['250m', '500m', '1km', 'ward_category'], overdue_threshold_days: 14 });
  const [overdueOnly, setOverdueOnly] = useState(false);

  const saveRadiusMode = (mode) => {
    api.put('/admin/tracker-settings', { similar_radius_mode: mode })
      .then((r) => { setTrackerCfg((c) => ({ ...c, ...r.data })); toast.success(`"Similar issues nearby" radius updated to ${RADIUS_LABELS[mode] || mode}`); })
      .catch(() => toast.error('Could not save the radius setting'));
  };

  const saveOverdueThreshold = (value) => {
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > 120) { toast.error('The follow-up threshold must be between 1 and 120 days'); return; }
    if (n === trackerCfg.overdue_threshold_days) return;
    api.put('/admin/tracker-settings', { overdue_threshold_days: n })
      .then((r) => { setTrackerCfg((c) => ({ ...c, ...r.data })); toast.success(`Follow-up threshold set to ${n} days`); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not save the threshold'));
  };

  const generateDigest = () => {
    setBusy(true);
    api.post('/admin/tracker-digest', {})
      .then((r) => toast.success(`Draft created: "${r.data.title}". Review and publish it from the Newsroom manager.`))
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not generate the digest'))
      .finally(() => setBusy(false));
  };

  const createResolutionStory = () => {
    if (!detail) return;
    setBusy(true);
    api.post(`/admin/issues/${detail.id}/resolution-story`)
      .then((r) => toast.success(`Draft story created: "${r.data.title}". Review the "What changed" section, then publish from the Newsroom manager.`))
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not create the resolution story'))
      .finally(() => setBusy(false));
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.set(k, v); });
    api.get(`/admin/issues?${params.toString()}`).then((r) => setIssues(r.data || [])).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/admin/issue-categories').then((r) => setCategories(r.data.categories || [])).catch(() => {});
    api.get('/communities').then((r) => setCommunities(r.data.communities || [])).catch(() => {});
    api.get('/admin/tracker-settings').then((r) => setTrackerCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  const openDetail = (issue) => {
    setDetail(issue);
    setNewStatus(issue.status);
    setNote('');
    setNotePublic(true);
    setEdit({
      public_title: issue.public_title || '',
      public_description: issue.public_description || '',
      ckl_case_number: issue.ckl_case_number || '',
      follow_up_date: issue.follow_up_date || '',
      map_hidden: !!issue.map_hidden,
      priority: issue.priority || 'normal',
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/admin/issues/${detail.id}`, edit);
      setDetail(res.data);
      toast.success('Issue details saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const addUpdate = async () => {
    if (!note.trim()) { toast.error('Write an update note'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/admin/issues/${detail.id}/updates`, { note, status: newStatus, public: notePublic });
      setDetail(res.data);
      setNote('');
      toast.success(notePublic ? 'Public timeline update posted' : 'Internal note added');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const convertToReport = async () => {
    try {
      const res = await api.post(`/admin/issues/${detail.id}/convert-to-report`);
      toast.success(`Added to the ${res.data.period} draft report (public-safe summary only)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/issues/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ward8-issues.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/issues/${deleting.id}`);
      toast.success('Issue deleted');
      setDeleting(null);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="admin-issues">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Issue Manager</h1>
        <div className="flex flex-wrap gap-2">
          <PinAuditPanel onChanged={load} />
          <RegeocodePanel onApplied={load} />
          <Button onClick={generateDigest} disabled={busy} variant="outline" className="h-11 font-bold btn-press border-[#C9A84C] text-[#1a2744]" data-testid="generate-digest-button"><Newspaper className="h-4 w-4 mr-1.5" /> Generate Monthly Digest</Button>
          <Button onClick={exportCsv} variant="outline" className="h-11 font-bold btn-press" data-testid="issues-export-csv"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">Ward 8 Public Issue Tracker reports with the full 9-step workflow. Public notes appear on the resident timeline; internal notes stay private.</p>

      {/* Resident status-change emails: secure SMTP setup + safe queue */}
      <ResidentEmailPanel />

      {/* Weekly digest generator */}
      <WeeklyDigestPanel />

      {/* Public map "Similar issues nearby" radius + overdue follow-up threshold */}
      <Card className="mb-4"><CardContent className="p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="tracker-radius-setting">
          <div className="flex-1">
            <p className="font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#C9A84C]" /> "Similar issues nearby" radius (public map)</p>
            <p className="text-xs text-slate-500 mt-0.5">Controls how the public tracker counts similar issues around each pin. Counts only — exact distances and private addresses are never shown.</p>
          </div>
          <Select value={trackerCfg.similar_radius_mode} onValueChange={saveRadiusMode}>
            <SelectTrigger className="h-11 bg-white w-full sm:w-64" data-testid="tracker-radius-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(trackerCfg.radius_options || []).map((v) => <SelectItem key={v} value={v}>{RADIUS_LABELS[v] || v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 border-t border-slate-100 pt-3" data-testid="overdue-threshold-setting">
          <div className="flex-1">
            <p className="font-bold text-[#1a2744] text-sm flex items-center gap-1.5"><CalendarRange className="h-4 w-4 text-[#C9A84C]" /> "Waiting on City" follow-up threshold (admin only)</p>
            <p className="text-xs text-slate-500 mt-0.5">Reports in Submitted to CKL or Waiting for CKL longer than this get an amber Overdue chip below. This is your personal follow-up threshold, not an official City response deadline. Reminders only — nothing is emailed to residents or the City.</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={120} key={trackerCfg.overdue_threshold_days} defaultValue={trackerCfg.overdue_threshold_days}
              onBlur={(e) => saveOverdueThreshold(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="h-11 bg-white w-24" data-testid="overdue-threshold-input" />
            <span className="text-sm font-semibold text-slate-600">days</span>
          </div>
        </div>
      </CardContent></Card>

      {/* Filters — collapsible on mobile */}
      <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 mb-4">
        <div className="flex gap-2">
          <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Search tracking #, title, name, email…" className="h-11 bg-white flex-1" data-testid="issues-search-input" />
          <Button variant="outline" onClick={() => setOverdueOnly(!overdueOnly)}
            className={`h-11 font-bold btn-press ${overdueOnly ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-100' : 'bg-white'}`}
            title="Show only reports waiting on the City past your follow-up threshold"
            data-testid="issues-overdue-filter">
            Overdue only{issues.filter((i) => (i.ckl_overdue_days || 0) > 0).length > 0 ? ` (${issues.filter((i) => (i.ckl_overdue_days || 0) > 0).length})` : ''}
          </Button>
          <Button variant="outline" onClick={() => setFiltersOpen(!filtersOpen)} className="h-11 bg-white font-bold btn-press sm:hidden" data-testid="issues-filters-toggle">
            Filters {filtersOpen ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-2 ${filtersOpen ? '' : 'hidden sm:grid'}`} data-testid="issues-filters">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-11 bg-white" data-testid="admin-issues-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_LABELS).filter(([v]) => v !== 'in_review').map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger className="h-11 bg-white" data-testid="issues-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.community} onValueChange={(v) => setFilters({ ...filters, community: v })}>
            <SelectTrigger className="h-11 bg-white" data-testid="issues-community-filter"><SelectValue placeholder="Community" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All communities</SelectItem>
              {communities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.visibility} onValueChange={(v) => setFilters({ ...filters, visibility: v })}>
            <SelectTrigger className="h-11 bg-white" data-testid="issues-visibility-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Public + private</SelectItem>
              <SelectItem value="public">Public only</SelectItem>
              <SelectItem value="private">Private only</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-11 bg-white" data-testid="issues-date-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-11 bg-white" data-testid="issues-date-to" />
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-3">
          {(overdueOnly ? issues.filter((i) => (i.ckl_overdue_days || 0) > 0) : issues).length === 0 && (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="issues-empty-state">
              {overdueOnly ? 'Nothing is past your follow-up threshold right now.' : 'No issues match these filters.'}
            </CardContent></Card>
          )}
          {(overdueOnly ? issues.filter((i) => (i.ckl_overdue_days || 0) > 0) : issues).map((issue) => (
            <button key={issue.id} onClick={() => openDetail(issue)} className="w-full text-left" data-testid="admin-issue-row">
              <Card className="card-lift">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-[#C9A84C]">{issue.tracking_number}</span>
                    <StatusBadge status={issue.status} />
                    {issue.ward && <span className="text-xs font-bold text-[#24406f] bg-[#EAF2FF] border border-[#CFE0FF] rounded-full px-2 py-0.5" data-testid="admin-issue-ward">Ward {issue.ward}</span>}
                    {issue.ward && Number(issue.ward) !== 8 && <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="admin-issue-outside-ward8">Outside Ward 8</span>}
                    {(issue.needs_location_review || issue.exact_lat == null) ? (
                      <span className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="admin-issue-needs-location">Needs location</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" title={issue.manually_located ? 'Manually verified by admin (protected from bulk re-geocoding)' : 'Verified location on file'} data-testid="admin-issue-location-verified"><CheckCircle2 className="h-3 w-3" /> Location verified</span>
                    )}
                    {issue.needs_ward_confirmation && <span className="text-xs font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="admin-issue-ward-confirm">Ward needs confirmation</span>}
                    {(issue.ckl_overdue_days || 0) > 0 && (
                      <span className="text-xs font-bold text-amber-900 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5"
                        title={`Waiting ${issue.ckl_waiting_days} days on the City, ${issue.ckl_overdue_days} days past your ${issue.ckl_threshold_days}-day follow-up threshold. Your reminder only, not an official City response deadline.`}
                        data-testid="admin-issue-overdue">
                        Overdue {issue.ckl_overdue_days}d · waiting {issue.ckl_waiting_days}d
                      </span>
                    )}
                    {issue.priority === 'high' && <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">High priority</span>}
                    {!issue.consent_public && <span className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Private</span>}
                    {issue.map_hidden && <span className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Hidden from map</span>}
                    {issue.follow_up_date && <span className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Follow-up {issue.follow_up_date}</span>}
                  </div>
                  <p className="font-semibold text-[#1a2744] mt-1 truncate">{issue.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{categoryLabel(issue.category)} · {issue.community || issue.location || 'No location'} · {formatDate(issue.created_at)}</p>
                  {(issue.name || issue.email) && <p className="text-xs text-slate-400 mt-0.5">Reporter: {issue.name || '—'} {issue.email && `· ${issue.email}`} {issue.phone && `· ${issue.phone}`}</p>}
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="admin-issue-dialog">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-[#C9A84C]">{detail.tracking_number}</span>
                  <StatusBadge status={detail.status} />
                </div>
                <DialogTitle className="font-serif text-[#1a2744] text-left">{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-700">{detail.description}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {detail.location || detail.community || 'No location provided'}</p>
                {/* Exact submitted location: ADMIN ONLY (never public) */}
                <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 text-xs text-slate-700 space-y-0.5" data-testid="admin-exact-location">
                  <p className="font-bold text-[#1a2744]">Exact submitted location (admin only)</p>
                  <p>Address: {detail.address || '—'}</p>
                  <p>Ward: {detail.ward ? `Ward ${detail.ward}` : 'Not determined'} · Geocode confidence: {detail.geocode_confidence || '—'}</p>
                  {detail.exact_lat != null && <p>Exact coordinates: {detail.exact_lat}, {detail.exact_lng}</p>}
                  {detail.lat != null && <p>Public (generalized) pin: {detail.lat}, {detail.lng}</p>}
                  {detail.geocode_display_name && <p className="text-slate-500">Matched: {detail.geocode_display_name}</p>}
                  {detail.needs_location_review && <p className="font-bold text-amber-800">Needs manual location review — not shown on the public map.</p>}
                  <RelocateControls detail={detail} onDone={(d) => { setDetail(d ? { ...detail, ...d } : detail); load(); }} />
                </div>
                {detail.photos?.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="admin-photos-panel">
                    <p className="font-bold text-[#1a2744] text-sm">Photos ({detail.photos.length}). Click to enlarge. {detail.photo_public ? 'Resident allowed public display; untick any photo to keep it off public pages and sharing.' : 'Resident did NOT allow public display: none are shown publicly.'}</p>
                    <PhotoGrid photos={detail.photos} caption={detail.tracking_number} testId="admin-photos" badge={(p) => (
                      <span className={`absolute top-1 left-1 text-[10px] font-bold rounded px-1.5 py-0.5 ${detail.photo_public && !(detail.photos_hidden || []).includes(p) ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'}`}>{detail.photo_public && !(detail.photos_hidden || []).includes(p) ? 'Public' : 'Private'}</span>
                    )} />
                    {detail.photo_public && (
                      <div className="flex flex-wrap gap-2">
                        {detail.photos.map((p, i) => {
                          const hidden = (detail.photos_hidden || []).includes(p);
                          return (
                            <label key={p} className="inline-flex items-center gap-1.5 text-xs text-slate-700 bg-white border border-slate-200 rounded-lg px-2 py-1.5 cursor-pointer">
                              <Checkbox checked={!hidden} onCheckedChange={async (v) => {
                                try {
                                  const r = await api.post(`/admin/issues/${detail.id}/photo-visibility`, { photo_url: p, hidden: !v });
                                  setDetail({ ...detail, photos_hidden: r.data.photos_hidden });
                                  toast.success(v ? `Photo ${i + 1} shown publicly` : `Photo ${i + 1} hidden from public pages`);
                                } catch (err) { toast.error(err.response?.data?.detail || 'Could not update photo'); }
                              }} data-testid={`admin-photo-public-toggle-${i}`} />
                              Photo {i + 1} public
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <IssueEmailHistory detail={detail} />
                <SharePreview issueId={detail.id} key={`share-${detail.id}-${detail.updated_at || ''}`} />

                {/* Editable public fields + case management */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <p className="font-bold text-[#1a2744] text-sm">Case management (editable)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <F label="Public title (shown on board/map)"><Input value={edit.public_title} onChange={(e) => setEdit({ ...edit, public_title: e.target.value })} className="h-10 bg-white" data-testid="issue-public-title-input" /></F>
                    <F label="CKL case number"><Input value={edit.ckl_case_number} onChange={(e) => setEdit({ ...edit, ckl_case_number: e.target.value })} className="h-10 bg-white" data-testid="issue-ckl-case-input" /></F>
                    <F label="Follow-up date"><Input type="date" value={edit.follow_up_date} onChange={(e) => setEdit({ ...edit, follow_up_date: e.target.value })} className="h-10 bg-white" data-testid="issue-followup-date-input" /></F>
                    <F label="Priority">
                      <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
                        <SelectTrigger className="h-10 bg-white" data-testid="admin-issue-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                  </div>
                  <F label="Public description (public-safe summary)"><Textarea value={edit.public_description} onChange={(e) => setEdit({ ...edit, public_description: e.target.value })} rows={2} className="bg-white" data-testid="issue-public-desc-input" /></F>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <Switch checked={edit.map_hidden} onCheckedChange={(v) => setEdit({ ...edit, map_hidden: v })} data-testid="issue-map-hidden-switch" /> Hide from public map
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={saveEdit} disabled={busy} className="h-10 bg-[#1a2744] text-white font-bold btn-press" data-testid="issue-save-edit-button"><Save className="h-4 w-4 mr-1.5" /> Save Details</Button>
                    <Button onClick={convertToReport} variant="outline" className="h-10 border-[#C9A84C] text-[#1a2744] font-bold btn-press" data-testid="issue-convert-report-button"><FileBarChart className="h-4 w-4 mr-1.5" /> Add to Monthly Report</Button>
                    {['resolved', 'closed'].includes(detail.status) && detail.consent_public && (
                      <Button onClick={createResolutionStory} disabled={busy} variant="outline" className="h-10 border-emerald-400 text-emerald-800 font-bold btn-press" data-testid="issue-resolution-story-button"><Award className="h-4 w-4 mr-1.5" /> Create Resolution Story</Button>
                    )}
                    <Button onClick={() => setDeleting(detail)} variant="outline" className="h-10 text-red-600 border-red-200 font-bold btn-press" data-testid="issue-delete-button">Delete</Button>
                  </div>
                </div>

                {/* Add update */}
                <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4 space-y-3">
                  <p className="font-bold text-[#1a2744] text-sm">Add update / change status</p>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="h-10 bg-white" data-testid="admin-issue-new-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_LABELS).filter(([v]) => v !== 'in_review').map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Update note (public notes appear on the resident timeline; internal notes stay admin-only)" rows={3} className="bg-white" data-testid="admin-issue-note-input" />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <Switch checked={notePublic} onCheckedChange={setNotePublic} data-testid="admin-issue-note-public" /> Public note
                    </label>
                    <Button onClick={addUpdate} disabled={busy} className="h-10 bg-[#1a2744] text-white font-bold btn-press" data-testid="admin-issue-add-update">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Post Update
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="font-bold text-sm text-[#1a2744] mb-2">History</p>
                  <div className="space-y-2">
                    {(detail.updates || []).slice().reverse().map((u) => (
                      <div key={u.id} className="border border-slate-100 rounded-lg p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge status={u.status} />
                          <span className="text-xs text-slate-400">{formatDate(u.date)}</span>
                          {!u.public && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Internal</span>}
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{u.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="issue-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete issue {deleting?.tracking_number}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the report, its photos, and its timeline. The resident will no longer be able to track it. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="issue-delete-confirm">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
